"use client";
import { useEffect, type CSSProperties, type PointerEvent } from "react";
import type { DesignElement } from "@/lib/design";
import { DEFAULT_FONT_FAMILY } from "@/lib/fonts";
import { retainCanvasFont } from "@/lib/canvas-fonts";
import { vectorPath } from "@/lib/vector";
import { VectorOverlay, type VectorEditingProps } from "./vector-overlay";
import type { AgentAction } from "@/lib/agent-activity";
import { useCanvasZoom } from "../canvas-camera";

function AgentOutline({ action, name }: { action: AgentAction; name: string }) {
  const zoom = useCanvasZoom();
  return <span className={`pf-agent-outline ${action.kind === "inspect" ? "is-inspect" : ""}`}
    style={{ "--canvas-zoom": zoom } as CSSProperties}
    role="img" aria-label={`${action.agentName}: ${action.message} — ${name}`}>
    <span>{action.agentName} · {action.kind === "inspect" ? "Inspected" : "Changed"}</span>
  </span>;
}
export function ElementVisual({
  element: e,
  elements,
  selected,
  onSelect,
  onPointerDown,
  onResize,
  preview = false,
  vectorEditing,
  onEditVector,
  agentHighlights,
}: {
  element: DesignElement;
  elements: DesignElement[];
  selected: string | null;
  onSelect: (id: string) => void;
  onPointerDown?: (ev: PointerEvent, id: string) => void;
  onResize?: (ev: PointerEvent, id: string) => void;
  preview?: boolean;
  vectorEditing?: VectorEditingProps;
  onEditVector?: (id: string) => void;
  agentHighlights?: Map<string, AgentAction>;
}) {
  const hasText = e.type === "text" || e.type === "button";
  useEffect(() => {
    if (hasText && !e.hidden) return retainCanvasFont(e);
  }, [hasText, e.hidden, e.fontFamily, e.fontUrl, e.fontWeight]);
  if (e.hidden) return null;
  const isSelected = !preview && selected === e.id;
  const agentAction = agentHighlights?.get(e.id);
  const style: CSSProperties = {
    position: "absolute",
    left: e.x,
    top: e.y,
    width: e.width,
    height: e.height,
    color: e.color,
    borderRadius:
      e.type === "path" ? 0 : e.type === "ellipse" ? "50%" : e.radius,
    opacity: e.opacity,
    transform: e.rotation ? `rotate(${e.rotation}deg)` : undefined,
    fontSize: e.fontSize,
    fontWeight: e.fontWeight,
    fontFamily: hasText ? e.fontFamily || DEFAULT_FONT_FAMILY : undefined,
    textAlign: e.textAlign,
    whiteSpace: "pre-wrap",
    lineHeight: 1.4,
    boxSizing: "border-box",
    overflow: "visible",
    cursor: preview ? "default" : e.locked ? "not-allowed" : "inherit",
  };
  return (
    <div
      data-element-id={e.id}
      style={style}
      className={`design-element ${isSelected ? "element-selected" : ""} ${!e.parentId && e.type === "frame" ? "root-frame" : ""}`}
      onDoubleClick={(ev) => {
        if (!preview && e.type === "path") {
          ev.stopPropagation();
          onEditVector?.(e.id);
        }
      }}
      onClick={(ev) => {
        if (!preview) {
          ev.stopPropagation();
          onSelect(e.id);
        }
      }}
      onPointerDown={(ev) => {
        if (!preview && onPointerDown) {
          ev.stopPropagation();
          onPointerDown(ev, e.id);
        }
      }}
    >
      {!e.parentId && e.type === "frame" && !preview && (
        <div className="frame-caption">
          <span>{e.name}</span>
          <span>
            {e.width} × {e.height}
          </span>
        </div>
      )}
      {/* Keep the object's editing controls outside its filtered artwork. */}
      <div
        className="element-artwork"
        style={{
          position: "absolute",
          inset: 0,
          boxSizing: "border-box",
          background: e.type === "path" ? "transparent" : e.fill,
          border:
            e.type !== "path" && e.stroke !== "transparent"
              ? `${e.strokeWidth}px solid ${e.stroke}`
              : undefined,
          borderRadius: "inherit",
          filter: e.blur > 0 ? `blur(${e.blur}px)` : undefined,
        }}
      >
        <div
          className="element-content"
          style={{
            position: "absolute",
            inset: 0,
            overflow: e.type === "path" ? "visible" : "hidden",
            borderRadius: "inherit",
            ...(e.type === "button"
              ? {
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }
              : {}),
          }}
        >
          {e.type === "path" && e.vector && (
            <svg
              width="100%"
              height="100%"
              viewBox={`0 0 ${e.width} ${e.height}`}
              style={{ overflow: "visible", display: "block" }}
              aria-label={e.name}
              role="img"
            >
              <path
                d={vectorPath(e.vector, e.width, e.height)}
                fill={e.fill}
                stroke={e.stroke}
                strokeWidth={e.strokeWidth}
                strokeLinecap={e.strokeCap || "round"}
                strokeLinejoin={e.strokeJoin || "round"}
              />
            </svg>
          )}
          {(e.type === "text" || e.type === "button") && e.text}
          {e.type === "image" &&
            (e.src ? (
              <img
                src={e.src}
                alt={e.name}
                draggable={false}
                style={{
                  width: "100%",
                  height: "100%",
                  objectFit: "cover",
                  borderRadius: "inherit",
                }}
              />
            ) : (
              <div className="image-placeholder">Image</div>
            ))}
          {e.type === "frame" &&
            elements
              .filter((child) => child.parentId === e.id)
              .map((child) => (
                <ElementVisual
                  key={child.id}
                  element={child}
                  elements={elements}
                  selected={selected}
                  onSelect={onSelect}
                  onPointerDown={onPointerDown}
                  onResize={onResize}
                  preview={preview}
                  vectorEditing={vectorEditing}
                  onEditVector={onEditVector}
                  agentHighlights={agentHighlights}
                />
              ))}
        </div>
      </div>
      {agentAction && <AgentOutline action={agentAction} name={e.name} />}
      {!preview && vectorEditing?.id === e.id && e.vector && (
        <VectorOverlay element={e} editing={vectorEditing} />
      )}
      {isSelected && vectorEditing?.id !== e.id && (
        <>
          <span className="selection-corner tl" />
          <span className="selection-corner tr" />
          <span className="selection-corner bl" />
          <button
            className="selection-corner br"
            aria-label="Resize element"
            onPointerDown={(ev) => {
              ev.stopPropagation();
              onResize?.(ev, e.id);
            }}
          />
          <span className="selection-size">
            {Math.round(e.width)} × {Math.round(e.height)}
          </span>
        </>
      )}
    </div>
  );
}
